import React, { useState, useEffect } from 'react';
import { ShoppingCart } from 'lucide-react';
import { PROMO, isPromoActive, timeRemaining } from '../data/promo-config';

/**
 * Sitewide URGENT banner that's visible during the active book promo window.
 * Shows headline + countdown + direct Amazon CTA.
 * Auto-disappears outside the promo window — no manual action needed.
 */
export default function PromoBanner() {
  const [active, setActive] = useState(() => isPromoActive());
  const [remaining, setRemaining] = useState(() => timeRemaining());
  const [hideOnPromoPage, setHideOnPromoPage] = useState(false);

  useEffect(() => {
    // Update countdown every minute (and re-check active state)
    const tick = () => {
      setActive(isPromoActive());
      setRemaining(timeRemaining());
    };
    tick();
    const interval = setInterval(tick, 60000);
    return () => clearInterval(interval);
  }, []);

  // Hide on the gift-read page (people are reading, don't double-up)
  useEffect(() => {
    const update = () => {
      const p = window.location.pathname;
      setHideOnPromoPage(p === '/gift/read');
    };
    update();
    window.addEventListener('popstate', update);
    const interval = setInterval(update, 800);
    return () => {
      window.removeEventListener('popstate', update);
      clearInterval(interval);
    };
  }, []);

  if (!active || hideOnPromoPage) return null;

  return (
    <a
      href={PROMO.amazonUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="promo-banner"
      data-testid="promo-banner"
      aria-label={`${PROMO.headline} — Download free on Amazon`}
    >
      <div className="promo-banner-inner">
        <span className="promo-pulse" aria-hidden="true" />
        <span className="promo-banner-tag">FREE</span>
        <span className="promo-banner-text">
          <strong>{PROMO.headline}</strong>
          <span className="promo-banner-sub">{PROMO.subline}</span>
        </span>
        {remaining && (
          <span className="promo-banner-timer" data-testid="promo-banner-timer">
            ⏳ {remaining}
          </span>
        )}
        <span className="promo-banner-cta">
          <ShoppingCart size={14} strokeWidth={2.4} />
          Download Free
        </span>
      </div>
    </a>
  );
}
